import { ButtonView, ButtonViewContainer, Container, FilterChips, FilterSelectMenu } from '@components/Filter';
import { FilterButton } from '@components/Filter/FilterButton';
import { FilterDrawer } from '@components/Filter/FilterDrawer';
import { hasValue } from '@components/Filter/filter.utils';
import { FlexRow } from '@components/FlexRow';
import InputDateRange from '@components/InputDateRange';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import {
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    styled
} from '@mui/material';
import { Roles } from '@utils/roleUtils';
import { useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

// ─── Types ────────────────────────────────────────────────────────────────────

export type IOrganizationUserFilter = {
    roles?: string[];
    isMemberActive?: boolean | null;
    active?: boolean | null;
    createdAt?: { start: string | null; end: string | null };
};

export const defaultOrganizationUserFilter: IOrganizationUserFilter = {
    roles: [],
    isMemberActive: null,
    active: null,
    createdAt: { start: null, end: null },
};

// ─── Labels ───────────────────────────────────────────────────────────────────

export const organizationUserFilterLabels: Record<string, string> = {
    roles: 'Rôle',
    isMemberActive: 'Statut invitation',
    active: 'Statut compte',
    createdAt: 'Date de création',
};

const roleOptions = [
    { label: 'Administrateur', value: Roles.ADMIN_PRO },
    { label: 'Membre', value: Roles.MEMBER_PRO },
    { label: 'Comptable', value: Roles.ACCOUNTANT_PRO },
];

const roleLabels: Record<string, string> = {
    [Roles.ADMIN_PRO]: 'Administrateur',
    [Roles.MEMBER_PRO]: 'Membre',
    [Roles.ACCOUNTANT_PRO]: 'Comptable',
};

const invitationLabels: Record<string, string> = {
    'true': 'Invitation acceptée',
    'false': 'En attente',
};

const accountLabels: Record<string, string> = {
    'true': 'Activé',
    'false': 'Désactivé',
};

// ─── Sort options ─────────────────────────────────────────────────────────────

export type IOrganizationUserSort = {
    field: string;
    order: 'asc' | 'desc';
};

// ─── Props ────────────────────────────────────────────────────────────────────

export type ViewType = 'list' | 'grid' | 'preview';

export interface OrganizationUserFilterProps {
    totalDocs: number;
    query: string;
    onQueryChange: (query: string) => void;
    sort: IOrganizationUserSort;
    onSortChange: (sort: IOrganizationUserSort) => void;
    filter: IOrganizationUserFilter;
    onFilterChange: (filter: IOrganizationUserFilter) => void;
    onAddMember?: () => void;
    viewType?: ViewType;
    onViewTypeChange?: (type: ViewType) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const OrganizationUserFilter = ({
    totalDocs,
    query,
    onQueryChange,
    sort,
    onSortChange,
    filter,
    onFilterChange,
    onAddMember,
    viewType,
    onViewTypeChange,
}: OrganizationUserFilterProps) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const methods = useForm<IOrganizationUserFilter>({
        defaultValues: defaultOrganizationUserFilter,
    });

    const toggleDrawer = (open: boolean) => () => {
        setIsDrawerOpen(open);
    };

    const onApplyFilter = (data: IOrganizationUserFilter) => {
        onFilterChange(data);
        setIsDrawerOpen(false);
    };

    const onResetFilter = () => {
        methods.reset(defaultOrganizationUserFilter);
        onFilterChange(defaultOrganizationUserFilter);
        setIsDrawerOpen(false);
    };

    const hasActiveFilters = useMemo(() => {
        return Object.values(filter).some(value => hasValue(value));
    }, [filter]);

    const renderLabel = ({ key, value }: { key: string; value: any }): string | undefined => {
        if (key === 'roles') {
            return roleLabels[value] ?? value;
        }
        if (key === 'isMemberActive') {
            return invitationLabels[String(value)] ?? String(value);
        }
        if (key === 'active') {
            return accountLabels[String(value)] ?? String(value);
        }
        return undefined;
    };

    return (
        <FormProvider {...methods}>
            <Container sx={{ mb: 2}}>
                <FlexRow sx={{ justifyContent: 'space-between', width: '100%', gap: 2, flexWrap: 'wrap' }}>
                    <FlexRow sx={{ gap: 2, flex: 1 }}>
                        <SearchBarWithNumber
                            placeholder="Rechercher par nom ou email..."
                            totalCount={totalDocs}
                            onChange={onQueryChange}
                        />
                        <FilterButton toggleDrawer={toggleDrawer} hasActiveFilters={hasActiveFilters} />
                    </FlexRow>
                    <SortControls direction="row" spacing={1}>
                        <FilterSelectMenu
                            size="small"
                            value={sort.field}
                            onChange={e => onSortChange({ ...sort, field: e.target.value as string })}
                        >
                            <MenuItem value="name">Nom</MenuItem>
                            <MenuItem value="createdDate">Date de création</MenuItem>
                        </FilterSelectMenu>
                        <FilterSelectMenu
                            size="small"
                            value={sort.order}
                            onChange={e => onSortChange({ ...sort, order: e.target.value as 'asc' | 'desc' })}
                        >
                            <MenuItem value="asc">
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <i className="fi fi-rr-sort-alpha-down" />
                                    <span>Croissant</span>
                                </Stack>
                            </MenuItem>
                            <MenuItem value="desc">
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <i className="fi fi-rr-sort-alpha-up" />
                                    <span>Décroissant</span>
                                </Stack>
                            </MenuItem>
                        </FilterSelectMenu>
                        <FlexRow>
                            <ButtonViewContainer>
                                <ButtonView onClick={() => onViewTypeChange && onViewTypeChange('list')} value="list" activeView={viewType === 'list'}>
                                    <i className='fi fi-br-list' />
                                </ButtonView>
                                <ButtonView onClick={() => onViewTypeChange && onViewTypeChange('grid')} value="grid" activeView={viewType === 'grid'}>
                                    <i className='fi fi-br-grid' />
                                </ButtonView>
                            </ButtonViewContainer>
                        </FlexRow>
                    </SortControls>
                </FlexRow>
            </Container>

            {hasActiveFilters && (
                <FlexRow sx={{ width: '100%', minWidth: 0, gap: '10px' }}>
                    <FilterChips
                        filter={filter}
                        labels={organizationUserFilterLabels}
                        defaultFilters={defaultOrganizationUserFilter}
                        onFilterChange={onFilterChange}
                        renderLabel={renderLabel}
                    />
                </FlexRow>
            )}

            <FilterDrawer
                isDrawerOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                onApplyFilter={onApplyFilter}
                onResetFilter={onResetFilter}
            >
                {/* Role */}
                <FormGroup>
                    <FormLabel>{organizationUserFilterLabels.roles}</FormLabel>
                    <Controller
                        name="roles"
                        control={methods.control}
                        render={({ field }) => (
                            <Stack direction="column">
                                {roleOptions.map(option => (
                                    <FormControlLabel
                                        key={option.value}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={(field.value ?? []).includes(option.value)}
                                                onChange={e => {
                                                    const current = field.value ?? [];
                                                    if (e.target.checked) {
                                                        field.onChange([...current, option.value]);
                                                    } else {
                                                        field.onChange(current.filter(v => v !== option.value));
                                                    }
                                                }}
                                            />
                                        }
                                        label={option.label}
                                    />
                                ))}
                            </Stack>
                        )}
                    />
                </FormGroup>

                {/* Invitation status */}
                <FormGroup>
                    <FormLabel>{organizationUserFilterLabels.isMemberActive}</FormLabel>
                    <Controller
                        name="isMemberActive"
                        control={methods.control}
                        render={({ field }) => (
                            <RadioGroup
                                value={field.value === null || field.value === undefined ? '' : String(field.value)}
                                onChange={e => {
                                    const val = e.target.value;
                                    field.onChange(val === '' ? null : val === 'true');
                                }}
                            >
                                <FormControlLabel value="" control={<Radio size="small" />} label="Tous" />
                                <FormControlLabel value="true" control={<Radio size="small" />} label="Invitation acceptée" />
                                <FormControlLabel value="false" control={<Radio size="small" />} label="En attente" />
                            </RadioGroup>
                        )}
                    />
                </FormGroup>

                {/* Account status */}
                <FormGroup>
                    <FormLabel>{organizationUserFilterLabels.active}</FormLabel>
                    <Controller
                        name="active"
                        control={methods.control}
                        render={({ field }) => (
                            <RadioGroup
                                value={field.value === null || field.value === undefined ? '' : String(field.value)}
                                onChange={e => {
                                    const val = e.target.value;
                                    field.onChange(val === '' ? null : val === 'true');
                                }}
                            >
                                <FormControlLabel value="" control={<Radio size="small" />} label="Tous" />
                                <FormControlLabel value="true" control={<Radio size="small" />} label="Activé" />
                                <FormControlLabel value="false" control={<Radio size="small" />} label="Désactivé" />
                            </RadioGroup>
                        )}
                    />
                </FormGroup>

                {/* Date range */}
                <FormGroup>
                    <FormLabel>{organizationUserFilterLabels.createdAt}</FormLabel>
                    <InputDateRange name="createdAt" />
                </FormGroup>
            </FilterDrawer>
        </FormProvider>
    );
};

// ─── Styled components ────────────────────────────────────────────────────────

const SortControls = styled(Stack)(({ theme }) => ({
    minWidth: 300,
    display: 'flex',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
        minWidth: 'auto',
    },
}));

export default OrganizationUserFilter;
